/**
 * AdminModal — the standard centered dialog for the admin portal,
 * matching /brand Aspect 15 (Modal). A dimmed backdrop, a raised card
 * with an optional eyebrow + display title + subtitle, scrollable body,
 * and a right-aligned footer for actions. Replaces the hand-rolled
 * `<Modal>` + overlay markup duplicated across disbursements, winners,
 * promos, inquiries, … Theme-driven — no hex.
 */
import type { ReactNode } from "react";
import { Modal, Platform, ScrollView, Text, View, type ViewStyle } from "react-native";

import { RADII, STROKES } from "../../../lib/brand";
import type { PaneTheme } from "../../brand/paneThemes";

export type AdminModalProps = {
  theme: PaneTheme;
  visible: boolean;
  onClose: () => void;
  title: string;
  /** Optional uppercase eyebrow above the title. */
  eyebrow?: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  children?: ReactNode;
  /** Right-aligned footer actions (buttons). */
  footer?: ReactNode;
  /** Max width of the dialog card. Default 480. */
  maxWidth?: number;
  /** Apply a frosted blur to the backdrop (web only). Default false. */
  blurBackdrop?: boolean;
};

export function AdminModal({
  theme,
  visible,
  onClose,
  title,
  eyebrow,
  subtitle,
  children,
  footer,
  maxWidth = 480,
  blurBackdrop = false,
}: AdminModalProps) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.65)",
            justifyContent: "center",
            paddingHorizontal: 16,
            paddingVertical: 24,
          },
          blurBackdrop && Platform.OS === "web"
            ? ({ backdropFilter: "blur(6px)" } as unknown as ViewStyle)
            : null,
        ]}
      >
        <View
          style={{
            width: "100%",
            maxWidth,
            maxHeight: "100%",
            alignSelf: "center",
            backgroundColor: theme.surfaceRaisedBg,
            borderRadius: RADII.lg,
            borderWidth: STROKES.hairline,
            borderColor: theme.borderStrong,
          }}
        >
          <ScrollView
            contentContainerStyle={{ padding: 22 }}
            showsVerticalScrollIndicator={false}
          >
            {eyebrow ? (
              <Text
                style={{
                  fontFamily: theme.bodySemiBoldFont,
                  fontSize: 10,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: theme.accent,
                  marginBottom: 8,
                }}
              >
                {eyebrow}
              </Text>
            ) : null}
            <Text
              style={{
                fontFamily: theme.displayMediumFont,
                fontSize: 22,
                lineHeight: 26,
                color: theme.textOnSurface,
              }}
            >
              {title}
            </Text>
            {subtitle ? (
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: theme.bodyFont,
                  fontSize: 13,
                  lineHeight: 20,
                  color: theme.textOnSurfaceDim,
                }}
              >
                {subtitle}
              </Text>
            ) : null}

            {children != null ? <View style={{ marginTop: 16 }}>{children}</View> : null}

            {footer != null ? (
              <View
                style={{
                  marginTop: 20,
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {footer}
              </View>
            ) : null}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}
